import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useModal } from '../../context/ModalContext';
import { shouldShowApiToast } from '../../utils/helpers';
import {
  getAutomations,
  getAutomation,
  createAutomation,
  updateAutomation,
  deleteAutomation,
  toggleAutomation,
  runAutomation,
  getAutomationStats,
  clearApiMessage,
  clearError,
  clearAutomationState,
  selectAutomations,
  selectAutomationPagination,
  selectCurrentAutomation,
  selectAutomationStats,
  selectAutomationsLoading,
  selectAutomationsApiMessage,
  selectAutomationsError,
} from '../../store/slices/main/automationSlice';
import { 
  getMembers, 
  selectMembers, 
  selectMemberPagination,
  selectAvailableGroups 
} from '../../store/slices/main/memberSlice';
import { 
  getVisitors, 
  selectVisitors,
  selectVisitorPagination 
} from '../../store/slices/growth/visitorSlice';
import { selectCurrentOrganization } from '../../store/slices/authSlice';
import { selectCurrentBranch } from '../../store/slices/system/branchSlice';
import { getMergeFieldValue } from '../../utils/mergeFieldValues';
import { Zap, Cake, Megaphone, Mail, HeartCrack, BarChart3, Lightbulb, Pencil, Trash2, Save, AlertTriangle, Clock, Tag, Search, Loader2, Inbox, CheckCircle2, Pause, Play, ClipboardList, Users, Users2, UserPlus } from 'lucide-react';

const Automation = ({ showToast }) => {
  const dispatch = useDispatch();
  const { openModal, closeModal } = useModal();
  
  // ==================== STATE ====================
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(4);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingRuleId, setEditingRuleId] = useState(null);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [availableBranches, setAvailableBranches] = useState([]);
  const [visitorSources, setVisitorSources] = useState([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const [dynamicAudienceOptions, setDynamicAudienceOptions] = useState([]);
  
  // ==================== SELECTORS ====================
  const members = useSelector(selectMembers) || [];
  const memberPagination = useSelector(selectMemberPagination) || {};
  const visitors = useSelector(selectVisitors) || [];
  const visitorPagination = useSelector(selectVisitorPagination) || {};
  const groups = useSelector(selectAvailableGroups) || [];
  const organization = useSelector(selectCurrentOrganization);
  const branch = useSelector(selectCurrentBranch);
  
  const automations = useSelector(selectAutomations) || [];
  const pagination = useSelector(selectAutomationPagination) || {
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0
  };
  const currentAutomation = useSelector(selectCurrentAutomation);
  const stats = useSelector(selectAutomationStats);
  const loading = useSelector(selectAutomationsLoading);
  const apiMessage = useSelector(selectAutomationsApiMessage);
  const error = useSelector(selectAutomationsError);

  // Refs for edit form
  const editTitleRef = useRef(null);
  const editDescRef = useRef(null);
  const editTriggerRef = useRef(null);
  const editSendTimeRef = useRef(null);
  const editAudienceRef = useRef(null);
  const editMessageRef = useRef(null);

  // Refs for create form
  const createTitleRef = useRef(null);
  const createDescRef = useRef(null);
  const createTriggerRef = useRef(null);
  const createSendTimeRef = useRef(null);
  const createAudienceRef = useRef(null);
  const createMessageRef = useRef(null);

  // Track last toast message to prevent duplicates
  const lastToastRef = useRef({ message: null, timestamp: 0 });

  // ==================== MERGE FIELDS ====================
  const mergeFields = [
    { key: '{FirstName}', label: 'First Name', description: "Member's first name", example: 'John' },
    { key: '{LastName}', label: 'Last Name', description: "Member's last name", example: 'Doe' },
    { key: '{FullName}', label: 'Full Name', description: "Member's full name", example: 'John Doe' },
    { key: '{ChurchName}', label: 'Church Name', description: 'Your church name', example: 'Grace Chapel' },
    { key: '{PastorName}', label: 'Pastor Name', description: "Pastor's name", example: 'Pastor Michael' },
    { key: '{Group}', label: 'Group', description: "Member's group", example: 'Youth Ministry' },
    { key: '{Branch}', label: 'Branch', description: "Member's branch", example: 'Main Campus' },
  ].map((field) => ({ ...field, example: getMergeFieldValue(field.key, { member: members[0], organization, branch }) }));

  // ==================== HELPERS ====================
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    }) + ' at ' + date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  // ==================== EFFECTS ====================

  // Load data on mount
  useEffect(() => {
    const loadData = async () => {
      setIsLoadingOptions(true);
      try {
        await Promise.all([
          dispatch(getAutomations({ 
            per_page: itemsPerPage, 
            page: currentPage,
            search: searchTerm,
            type: typeFilter
          })),
          dispatch(getAutomationStats()),
          dispatch(getMembers({ per_page: 50 })),
          dispatch(getVisitors({ per_page: 50 })),
        ]);
      } catch (err) {
        console.error('Error loading automations:', err);
      } finally {
        setIsLoadingOptions(false);
      }
    };
    
    loadData();
    
    return () => {
      dispatch(clearAutomationState());
    };
  }, [dispatch]);

  // Build dynamic audience options
  useEffect(() => {
    buildDynamicAudienceOptions();
  }, [members, visitors, groups, memberPagination, visitorPagination]);

  // Handle filters with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      const params = {
        per_page: itemsPerPage,
        page: currentPage,
        search: searchTerm
      };
      if (typeFilter) params.type = typeFilter;
      
      dispatch(getAutomations(params));
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm, typeFilter, currentPage, itemsPerPage, dispatch]);

  // Display API messages - with deduplication (same as Members component)
  useEffect(() => {
    if (!apiMessage || !showToast || !shouldShowApiToast(apiMessage, !!error)) return;
    
    const messageKey = `${apiMessage}_${error ? 'error' : 'success'}`;
    const now = Date.now();
    
    if (lastToastRef.current.message === messageKey && 
        (now - lastToastRef.current.timestamp) < 2000) {
      return;
    }
    
    lastToastRef.current = { message: messageKey, timestamp: now };
    
    const toastType = error ? 'error' : 'success';
    showToast(apiMessage, toastType);
    
    setTimeout(() => {
      dispatch(clearApiMessage());
      dispatch(clearError());
    }, 500);
  }, [apiMessage, error, dispatch, showToast]);

  // ==================== TOAST HELPER ====================
  const showToastMessage = useCallback((message, type = 'info') => {
    if (showToast && typeof showToast === 'function') {
      showToast(message, type);
    }
  }, [showToast]);

  // ==================== BUILD DYNAMIC AUDIENCE OPTIONS ====================
  
  const buildDynamicAudienceOptions = () => {
    const options = [];
    
    // 1. MEMBER OPTIONS
    const memberCount = memberPagination?.total || members.length || 0;
    const activeMemberCount = members.filter(m => m.status === 'Active').length;
    const inactiveMemberCount = members.filter(m => m.status === 'Inactive').length;
    const visitorCount = visitorPagination?.total || visitors.length || 0;
    
    options.push(
      { id: 'all_members', value: 'All Members', label: `🌐 All Members (${memberCount})`, type: 'members' },
      { id: 'active_members', value: 'Active Members', label: `✅ Active Members (${activeMemberCount})`, type: 'members' },
      { id: 'inactive_members', value: 'Inactive Members', label: `⭕ Inactive Members (${inactiveMemberCount})`, type: 'members' },
      { id: 'all_visitors', value: 'All Visitors', label: `👋 All Visitors (${visitorCount})`, type: 'visitors' }
    );
    
    // 2. MEMBER GROUPS
    let groupsList = [];
    
    // Try from member pagination
    if (memberPagination?.groups && Array.isArray(memberPagination.groups)) {
      groupsList = memberPagination.groups;
    } 
    // Try from groups selector
    else if (groups && groups.length > 0) {
      groupsList = groups;
    }
    // Extract from members data
    else if (members && members.length > 0) {
      const uniqueGroups = new Set();
      members.forEach(m => {
        if (m.group) uniqueGroups.add(m.group);
      });
      groupsList = Array.from(uniqueGroups);
    }
    
    // Add member groups to options
    groupsList.forEach(group => {
      const groupMemberCount = members.filter(m => m.group === group).length;
      options.push({
        id: `group_${group}`,
        value: group,
        label: `👥 ${group} (${groupMemberCount} members)`,
        type: 'group'
      });
    });
    
    // 3. VISITOR SOURCES
    const sources = new Set();
    visitors.forEach(v => {
      if (v.source) sources.add(v.source);
      if (v.referred_by) sources.add(v.referred_by);
      if (v.visitor_type) sources.add(v.visitor_type);
    });
    
    Array.from(sources).filter(Boolean).forEach(source => {
      const sourceCount = visitors.filter(v => 
        v.source === source || v.referred_by === source || v.visitor_type === source
      ).length;
      options.push({
        id: `source_${source}`,
        value: `Source: ${source}`,
        label: `📌 ${source} (${sourceCount} visitors)`,
        type: 'visitor_source'
      });
    });
    
    // 4. BRANCHES
    const branches = new Set();
    members.forEach(m => {
      if (m.branch) branches.add(m.branch);
    });
    visitors.forEach(v => {
      if (v.branch) branches.add(v.branch);
    });
    if (branch?.name) branches.add(branch.name);
    
    Array.from(branches).filter(Boolean).forEach(branchName => {
      const memberCount = members.filter(m => m.branch === branchName).length;
      const visitorCount = visitors.filter(v => v.branch === branchName).length;
      const total = memberCount + visitorCount;
      options.push({
        id: `branch_${branchName}`,
        value: `Branch: ${branchName}`,
        label: `🏛️ ${branchName} (${total} people)`,
        type: 'branch'
      });
    });
    
    // 5. RECENT/NEW OPTIONS
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    
    const newMembers = members.filter(m => {
      const joinedDate = new Date(m.created_at || m.joined_date);
      return joinedDate >= thirtyDaysAgo;
    });
    if (newMembers.length > 0) {
      options.push({
        id: 'new_members',
        value: 'New Members',
        label: `🌟 New Members (${newMembers.length})`,
        type: 'members'
      });
    }
    
    const recentVisitors = visitors.filter(v => {
      const visitedDate = new Date(v.visited_at || v.created_at);
      return visitedDate >= thirtyDaysAgo;
    });
    if (recentVisitors.length > 0) {
      options.push({
        id: 'recent_visitors',
        value: 'Recent Visitors',
        label: `🔔 Recent Visitors (${recentVisitors.length})`,
        type: 'visitors'
      });
    }
    
    setDynamicAudienceOptions(options);
  };

  // ==================== HELPERS ====================

  // Filter rules locally for search
  const filteredRules = automations.filter(rule => {
    const matchesSearch = rule.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          rule.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = !typeFilter || rule.type === typeFilter;
    return matchesSearch && matchesType;
  });

  // Pagination calculations
  const totalItems = filteredRules.length;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredRules.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    
    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }
    return pageNumbers;
  };

  const insertField = (textareaRef, field) => {
    if (!textareaRef.current) return;
    
    const textarea = textareaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const value = textarea.value;
    textarea.value = value.substring(0, start) + field + value.substring(end);
    textarea.focus();
    textarea.selectionStart = textarea.selectionEnd = start + field.length;
  };

  const getEditFormValues = () => {
    return {
      title: editTitleRef.current?.value || '',
      description: editDescRef.current?.value || '',
      type: editTriggerRef.current?.value || '',
      send_time: editSendTimeRef.current?.value || '',
      audience: editAudienceRef.current?.value || '',
      message_template: editMessageRef.current?.value || ''
    };
  };

  const getCreateFormValues = () => {
    return {
      title: createTitleRef.current?.value || '',
      description: createDescRef.current?.value || '',
      type: createTriggerRef.current?.value || '',
      send_time: createSendTimeRef.current?.value || '',
      audience: createAudienceRef.current?.value || '',
      message_template: createMessageRef.current?.value || ''
    };
  };

  // ==================== AUTOMATION CRUD ====================

  const openNewAutomation = () => {
    // Set default date-time to now + 1 hour
    const defaultDateTime = new Date();
    defaultDateTime.setHours(defaultDateTime.getHours() + 1);
    const defaultDateTimeStr = defaultDateTime.toISOString().slice(0, 16);
    
    setTimeout(() => {
      if (createTitleRef.current) createTitleRef.current.value = '';
      if (createDescRef.current) createDescRef.current.value = '';
      if (createTriggerRef.current) createTriggerRef.current.value = '';
      if (createSendTimeRef.current) createSendTimeRef.current.value = defaultDateTimeStr;
      if (createAudienceRef.current) createAudienceRef.current.value = '';
      if (createMessageRef.current) createMessageRef.current.value = '';
    }, 0);

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Zap size={16} style={{ verticalAlign: '-3px' }} /> New Automation Rule</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div className="steps" style={{ display: 'flex', alignItems: 'center', marginBottom: '24px' }}>
            <div className="step" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div className="step-circle active" style={{ width: '28px', height: '28px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--navy)', color: 'white' }}>1</div>
              <div className="step-label" style={{ fontSize: '12px', fontWeight: '500' }}>Trigger</div>
            </div>
            <div className="step-line" style={{ flex: 1, height: '1px', background: 'var(--border)', margin: '0 8px' }}></div>
            <div className="step" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div className="step-circle pending" style={{ width: '28px', height: '28px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--border)', color: 'var(--text-light)' }}>2</div>
              <div className="step-label" style={{ fontSize: '12px', fontWeight: '500' }}>Audience</div>
            </div>
            <div className="step-line" style={{ flex: 1, height: '1px', background: 'var(--border)', margin: '0 8px' }}></div>
            <div className="step" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div className="step-circle pending" style={{ width: '28px', height: '28px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--border)', color: 'var(--text-light)' }}>3</div>
              <div className="step-label" style={{ fontSize: '12px', fontWeight: '500' }}>Message</div>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Automation Name <span style={{ color: 'var(--red)' }}>*</span></label>
            <input 
              className="form-input" 
              placeholder="e.g. Birthday Greeting" 
              ref={createTitleRef}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Description</label>
            <input 
              className="form-input" 
              placeholder="Brief description of the automation" 
              ref={createDescRef}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Trigger Type <span style={{ color: 'var(--red)' }}>*</span></label>
            <select 
              className="form-select" 
              ref={createTriggerRef}
              defaultValue=""
            >
              <option value="">Select Trigger</option>
              <option value="birthday">🎂 Birthday</option>
              <option value="broadcast">📢 Scheduled Broadcast</option>
              <option value="drip">📧 Drip Campaign</option>
              <option value="absentee">💔 Absentee Check-In</option>
              <option value="poll">📊 Poll</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Schedule Date & Time</label>
            <input 
              className="form-input" 
              type="datetime-local" 
              ref={createSendTimeRef}
              defaultValue={defaultDateTimeStr}
            />
            <div className="form-hint">Leave blank to run immediately or schedule for later</div>
          </div>
          <div className="form-group">
            <label className="form-label">Target Audience</label>
            <select 
              className="form-select" 
              ref={createAudienceRef}
              defaultValue=""
            >
              <option value="">Select Audience</option>
              {isLoadingOptions ? (
                <option value="" disabled>Loading audiences...</option>
              ) : dynamicAudienceOptions.length > 0 ? (
                dynamicAudienceOptions.map(option => (
                  <option key={option.id} value={option.value}>
                    {option.label}
                  </option>
                ))
              ) : (
                <option value="" disabled>No audiences available</option>
              )}
            </select>
            <div className="form-hint" style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              <span>👥 {members.length} members loaded</span>
              <span>👋 {visitors.length} visitors loaded</span>
              <span>📊 {dynamicAudienceOptions.length} audience options</span>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Message Template <span style={{ color: 'var(--red)' }}>*</span></label>
            <textarea 
              className="form-textarea" 
              rows="4" 
              ref={createMessageRef}
              placeholder="Dear {FirstName}, …"
            />
            <div className="form-hint">
              Use {'{FirstName}'}, {'{LastName}'}, {'{FullName}'}, {'{ChurchName}'}, {'{PastorName}'} for personalisation
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Available Merge Fields</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {mergeFields.map(field => (
                <button 
                  key={field.key}
                  className="btn btn-ghost btn-sm" 
                  onClick={() => insertField(createMessageRef, field.key)}
                  title={`${field.label}: ${field.description} (e.g. ${field.example})`}
                >
                  {field.key}
                </button>
              ))}
            </div>
            <div style={{ fontSize: '11px', color: 'var(--text-light)', marginTop: '4px' }}>
              <Lightbulb size={14} style={{ verticalAlign: '-3px' }} /> Click a merge field to insert it at the cursor position
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-gold btn-sm" 
            disabled={isSubmitting}
            onClick={async () => {
              const values = getCreateFormValues();
              
              if (!values.title.trim()) {
                showToastMessage('Please enter an automation name', 'error');
                return;
              }
              if (!values.type) {
                showToastMessage('Please select a trigger type', 'error');
                return;
              }
              if (!values.message_template.trim()) {
                showToastMessage('Please enter a message template', 'error');
                return;
              }
              
              setIsSubmitting(true);
              try {
                const result = await dispatch(createAutomation(values));
                if (result.payload?.success) {
                  closeModal();
                  await dispatch(getAutomations({ per_page: itemsPerPage, page: currentPage }));
                  await dispatch(getAutomationStats());
                  showToastMessage('⚡ Automation created and activated!', 'success');
                } else if (result.error) {
                  showToastMessage(result.payload?.message || 'Failed to create automation', 'error');
                }
              } catch (err) {
                showToastMessage('Failed to create automation', 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {isSubmitting ? 'Creating...' : 'Create & Activate'}
          </button>
        </div>
      </div>
    );
  };

  const handleEditAutomation = (rule) => {
    setEditingRuleId(rule.id);
    
    setTimeout(() => {
      if (editTitleRef.current) editTitleRef.current.value = rule.title || '';
      if (editDescRef.current) editDescRef.current.value = rule.description || '';
      if (editTriggerRef.current) editTriggerRef.current.value = rule.type || '';
      if (editSendTimeRef.current) {
        if (rule.send_time) {
          try {
            const date = new Date(rule.send_time);
            if (!isNaN(date.getTime())) {
              editSendTimeRef.current.value = date.toISOString().slice(0, 16);
            }
          } catch (e) {
            editSendTimeRef.current.value = '';
          }
        } else {
          editSendTimeRef.current.value = '';
        }
      }
      if (editAudienceRef.current) editAudienceRef.current.value = rule.audience || '';
      if (editMessageRef.current) editMessageRef.current.value = rule.message_template || '';
    }, 50);

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Pencil size={16} style={{ verticalAlign: '-3px' }} /> Edit Automation: {rule.title}</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Automation Name <span style={{ color: 'var(--red)' }}>*</span></label>
            <input 
              className="form-input" 
              ref={editTitleRef}
              defaultValue={rule.title || ''}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Description</label>
            <input 
              className="form-input" 
              ref={editDescRef}
              defaultValue={rule.description || ''}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Trigger Type <span style={{ color: 'var(--red)' }}>*</span></label>
            <select 
              className="form-select" 
              ref={editTriggerRef}
              defaultValue={rule.type || ''}
            >
              <option value="">Select Trigger</option>
              <option value="birthday">🎂 Birthday</option>
              <option value="broadcast">📢 Scheduled Broadcast</option>
              <option value="drip">📧 Drip Campaign</option>
              <option value="absentee">💔 Absentee Check-In</option>
              <option value="poll">📊 Poll</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Schedule Date & Time</label>
            <input 
              className="form-input" 
              type="datetime-local" 
              ref={editSendTimeRef}
              defaultValue=""
            />
            <div className="form-hint">Leave blank to run immediately or schedule for later</div>
          </div>
          <div className="form-group">
            <label className="form-label">Target Audience</label>
            <select 
              className="form-select" 
              ref={editAudienceRef}
              defaultValue={rule.audience || ''}
            >
              <option value="">Select Audience</option>
              {isLoadingOptions ? (
                <option value="" disabled>Loading audiences...</option>
              ) : dynamicAudienceOptions.length > 0 ? (
                dynamicAudienceOptions.map(option => (
                  <option key={option.id} value={option.value}>
                    {option.label}
                  </option>
                ))
              ) : (
                <option value="" disabled>No audiences available</option>
              )}
            </select>
            <div className="form-hint" style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              <span>👥 {members.length} members loaded</span>
              <span>👋 {visitors.length} visitors loaded</span>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Message Template <span style={{ color: 'var(--red)' }}>*</span></label>
            <textarea 
              className="form-textarea" 
              rows="4" 
              ref={editMessageRef}
              defaultValue={rule.message_template || ''}
            />
            <div className="form-hint">
              Use {'{FirstName}'}, {'{LastName}'}, {'{FullName}'}, {'{ChurchName}'}, {'{PastorName}'} for personalisation
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Available Merge Fields</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {mergeFields.map(field => (
                <button 
                  key={field.key}
                  className="btn btn-ghost btn-sm" 
                  onClick={() => insertField(editMessageRef, field.key)}
                  title={`${field.label}: ${field.description} (e.g. ${field.example})`}
                >
                  {field.key}
                </button>
              ))}
            </div>
            <div style={{ fontSize: '11px', color: 'var(--text-light)', marginTop: '4px' }}>
              <Lightbulb size={14} style={{ verticalAlign: '-3px' }} /> Click a merge field to insert it at the cursor position
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-gold btn-sm" 
            disabled={isSubmitting}
            onClick={async () => {
              const values = getEditFormValues();
              
              if (!values.title.trim()) {
                showToastMessage('Please enter an automation name', 'error');
                return;
              }
              if (!values.type) {
                showToastMessage('Please select a trigger type', 'error');
                return;
              }
              if (!values.message_template.trim()) {
                showToastMessage('Please enter a message template', 'error');
                return;
              }
              
              setIsSubmitting(true);
              try {
                const result = await dispatch(updateAutomation({ 
                  id: rule.id, 
                  data: values 
                }));
                if (result.payload?.success) {
                  closeModal();
                  await dispatch(getAutomations({ per_page: itemsPerPage, page: currentPage }));
                  await dispatch(getAutomationStats());
                  showToastMessage(`✅ Automation "${values.title}" updated!`, 'success');
                } else if (result.error) {
                  showToastMessage(result.payload?.message || 'Failed to update automation', 'error');
                }
              } catch (err) {
                showToastMessage('Failed to update automation', 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {isSubmitting ? 'Saving...' : <><Save size={16} style={{ verticalAlign: '-3px' }} /> Save Changes</>}
          </button>
        </div>
      </div>
    );
  };

  const handleDeleteAutomation = (rule) => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Trash2 size={16} style={{ verticalAlign: '-3px' }} /> Delete Automation</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Are you sure you want to delete <strong>{rule.title}</strong>?
          </p>
          <div style={{ 
            padding: '12px', 
            background: 'var(--red-bg)', 
            borderRadius: '8px', 
            fontSize: '13px', 
            color: 'var(--red)' 
          }}>
            <AlertTriangle size={14} style={{ verticalAlign: '-2px' }} /> This action cannot be undone. All associated data will be removed.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-danger btn-sm" 
            disabled={isSubmitting}
            onClick={async () => {
              setIsSubmitting(true);
              try {
                const result = await dispatch(deleteAutomation(rule.id));
                if (result.payload?.success) {
                  closeModal();
                  await dispatch(getAutomations({ per_page: itemsPerPage, page: currentPage }));
                  await dispatch(getAutomationStats());
                  showToastMessage(`🗑️ "${rule.title}" deleted`, 'error');
                } else if (result.error) {
                  showToastMessage(result.payload?.message || 'Failed to delete automation', 'error');
                }
              } catch (err) {
                showToastMessage('Failed to delete automation', 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            Delete Automation
          </button>
        </div>
      </div>
    );
  };

  const handleToggleAutomation = async (id, active) => {
    try {
      const result = await dispatch(toggleAutomation(id));
      if (result.payload?.success) {
        await dispatch(getAutomations({ per_page: itemsPerPage, page: currentPage }));
        const action = active ? 'activated' : 'paused';
        showToastMessage(`Automation ${action} successfully`, active ? 'success' : 'info');
      }
    } catch (err) {
      showToastMessage('Failed to toggle automation', 'error');
    }
  };

  const handleRunAutomation = async (rule) => {
    if (!rule.is_active) {
      showToastMessage('Please activate the automation first', 'error');
      return;
    }

    // Find the audience label
    const audienceOption = dynamicAudienceOptions.find(opt => opt.value === rule.audience);
    const audienceLabel = audienceOption?.label || rule.audience || 'All Members';

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Zap size={16} style={{ verticalAlign: '-3px' }} /> Run Automation: {rule.title}</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Are you sure you want to manually run <strong>{rule.title}</strong>?
          </p>
          <div style={{ 
            padding: '12px', 
            background: 'var(--cream)', 
            borderRadius: '8px', 
            fontSize: '13px', 
            color: 'var(--text-mid)' 
          }}>
            <div><BarChart3 size={14} style={{ verticalAlign: '-2px' }} /> Audience: <strong>{audienceLabel}</strong></div>
            <div><Tag size={14} style={{ verticalAlign: '-2px' }} /> Type: <strong>{rule.type}</strong></div>
            {rule.send_time && (
              <div><Clock size={14} style={{ verticalAlign: '-2px' }} /> Scheduled: <strong>{formatDateTime(rule.send_time)}</strong></div>
            )}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-gold btn-sm" 
            disabled={isSubmitting}
            onClick={async () => {
              setIsSubmitting(true);
              try {
                const result = await dispatch(runAutomation(rule.id));
                if (result.payload?.success) {
                  closeModal();
                  await dispatch(getAutomations({ per_page: itemsPerPage, page: currentPage }));
                  await dispatch(getAutomationStats());
                  showToastMessage(`✅ Automation run successfully! Sent to ${result.payload?.result?.sent || 0} members`, 'success');
                } else if (result.error) {
                  showToastMessage(result.payload?.message || 'Failed to run automation', 'error');
                }
              } catch (err) {
                showToastMessage('Failed to run automation', 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            Run Automation
          </button>
        </div>
      </div>
    );
  };

  // ==================== RENDER ====================

  const statsData = stats || { total: 0, active: 0, inactive: 0, total_sent: 0 };

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ fontSize: '14px', color: 'var(--text-mid)' }}>
          <Zap size={16} style={{ verticalAlign: '-3px' }} /> Set up automated SMS rules that run without any manual action.
        </div>
        <button className="btn btn-gold btn-sm" onClick={openNewAutomation} disabled={isSubmitting}>
          + New Automation
        </button>
      </div>

      <div className="grid-2" style={{ marginBottom: '20px' }}>
        <div className="card">
          <div style={{ display: 'flex', gap: '14px', alignItems: 'center' }}>
            <div style={{ width: '50px', height: '50px', background: 'var(--green-bg)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Zap size={24} /></div>
            <div>
              <div style={{ fontSize: '24px', fontWeight: '700', fontFamily: 'Playfair Display, serif', color: 'var(--navy)' }}>{statsData.active || 0}</div>
              <div style={{ fontSize: '13px', color: 'var(--text-light)' }}>Active Automations</div>
            </div>
          </div>
        </div>
        <div className="card">
          <div style={{ display: 'flex', gap: '14px', alignItems: 'center' }}>
            <div style={{ width: '50px', height: '50px', background: 'var(--blue-bg)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Mail size={24} /></div>
            <div>
              <div style={{ fontSize: '24px', fontWeight: '700', fontFamily: 'Playfair Display, serif', color: 'var(--navy)' }}>{statsData.total_sent?.toLocaleString() || 0}</div>
              <div style={{ fontSize: '13px', color: 'var(--text-light)' }}>Total Auto-Messages Sent</div>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header" style={{ flexWrap: 'wrap', gap: '12px' }}>
          <div className="card-title"><ClipboardList size={16} style={{ verticalAlign: '-3px' }} /> Automation Rules</div>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <div className="search-box" style={{ maxWidth: '200px' }}>
              <span className="search-icon"><Search size={14} /></span>
              <input 
                type="text" 
                placeholder="Search automations…"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
            <select 
              className="form-select" 
              style={{ width: 'auto', padding: '6px 10px', fontSize: '12px' }}
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">All Types</option>
              <option value="birthday">Birthday</option>
              <option value="broadcast">Broadcast</option>
              <option value="drip">Drip</option>
              <option value="absentee">Absentee</option>
              <option value="poll">Poll</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Loader2 size={40} style={{ animation: 'spin 1s linear infinite', color: 'var(--text-light)' }} />
            <div style={{ color: 'var(--text-light)', marginTop: '8px' }}>Loading automations...</div>
          </div>
        ) : currentItems.length > 0 ? (
          <>
            {currentItems.map(a => {
              const colorMap = {
                birthday: '#f59e0b',
                broadcast: '#3b82f6',
                drip: '#8b5cf6',
                absentee: '#ef4444',
                poll: '#22c55e'
              };
              const iconMap = {
                birthday: Cake,
                broadcast: Megaphone,
                drip: Mail,
                absentee: HeartCrack,
                poll: BarChart3
              };
              const RuleIcon = iconMap[a.type] || Zap;
              
              // Find audience label
              const audienceOption = dynamicAudienceOptions.find(opt => opt.value === a.audience);
              const audienceLabel = audienceOption?.label || a.audience || 'All Members';

              return (
                <div key={a.id} className="automation-rule" style={{ 
                  border: '1px solid var(--border)',
                  borderRadius: '10px',
                  padding: '14px',
                  marginBottom: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  flexWrap: 'wrap'
                }}>
                  <div className="rule-icon" style={{ 
                    width: '38px', 
                    height: '38px', 
                    borderRadius: '8px', 
                    background: colorMap[a.type] || '#6b7280',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <RuleIcon size={18} color="white" />
                  </div>
                  <div style={{ flex: 1, minWidth: '150px' }}>
                    <div className="rule-title" style={{ fontSize: '13px', fontWeight: '600', color: 'var(--navy)' }}>
                      {a.title}
                      <span className="badge badge-navy" style={{ marginLeft: '8px', fontSize: '9px' }}>{a.type}</span>
                    </div>
                    <div className="rule-desc" style={{ fontSize: '12px', color: 'var(--text-mid)' }}>{a.description}</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-light)', marginTop: '4px' }}>
                      Audience: <strong>{audienceLabel}</strong>
                      {a.send_time && (
                        <span style={{ marginLeft: '12px' }}>
                          <Clock size={12} style={{ verticalAlign: '-2px' }} /> {formatDateTime(a.send_time)}
                        </span>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                    {/* Status Toggle */}
                    <button 
                      className={`btn ${a.is_active ? 'btn-success' : 'btn-secondary'} btn-sm`}
                      onClick={() => handleToggleAutomation(a.id, !a.is_active)}
                      style={{ minWidth: '65px' }}
                    >
                      {a.is_active ? <><CheckCircle2 size={14} style={{ verticalAlign: '-2px' }} /> Active</> : <><Pause size={14} style={{ verticalAlign: '-2px' }} /> Paused</>}
                    </button>

                    {/* Run Button */}
                    <button
                      className="btn btn-gold btn-sm"
                      onClick={() => handleRunAutomation(a)}
                      disabled={!a.is_active}
                      title={!a.is_active ? 'Activate first to run' : 'Run automation now'}
                    >
                      <Play size={14} style={{ verticalAlign: '-2px' }} /> Run
                    </button>

                    {/* Edit Button */}
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={() => handleEditAutomation(a)}
                    >
                      <Pencil size={16} />
                    </button>

                    {/* Delete Button */}
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={() => handleDeleteAutomation(a)}
                      style={{ color: 'var(--red)' }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              );
            })}
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                paddingTop: '16px',
                borderTop: '1px solid var(--border)',
                marginTop: '8px',
                flexWrap: 'wrap',
                gap: '10px'
              }}>
                <div style={{ fontSize: '13px', color: 'var(--text-light)' }}>
                  Showing {indexOfFirstItem + 1}–{Math.min(indexOfLastItem, totalItems)} of {totalItems} automations
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                  <button 
                    className="btn btn-ghost btn-sm"
                    onClick={goToPreviousPage}
                    disabled={currentPage === 1}
                    style={{ padding: '4px 10px', fontSize: '12px' }}
                  >
                    ←
                  </button>
                  {getPageNumbers().map(num => (
                    <button 
                      key={num}
                      className={`btn ${num === currentPage ? 'btn-gold' : 'btn-ghost'} btn-sm`}
                      onClick={() => goToPage(num)}
                      style={{ padding: '4px 10px', fontSize: '12px', minWidth: '28px' }}
                    >
                      {num}
                    </button>
                  ))}
                  <button 
                    className="btn btn-ghost btn-sm"
                    onClick={goToNextPage}
                    disabled={currentPage === totalPages}
                    style={{ padding: '4px 10px', fontSize: '12px' }}
                  >
                    →
                  </button>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <label style={{ fontSize: '12px', color: 'var(--text-light)' }}>Per page:</label>
                  <select 
                    className="form-select"
                    style={{ width: 'auto', padding: '4px 8px', fontSize: '12px' }}
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                  >
                    <option value={4}>4</option>
                    <option value={8}>8</option>
                    <option value={12}>12</option>
                    <option value={20}>20</option>
                  </select>
                </div>
              </div>
            )}
          </>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <div style={{ marginBottom: '12px' }}><Inbox size={48} strokeWidth={1.5} /></div>
            <div style={{ fontWeight: '600', marginBottom: '6px' }}>No automations found</div>
            <div style={{ color: 'var(--text-light)', fontSize: '14px', marginBottom: '16px' }}>
              {searchTerm || typeFilter ? 'Try adjusting your filters' : 'Create your first automation rule'}
            </div>
            {!searchTerm && !typeFilter && (
              <button className="btn btn-gold btn-sm" onClick={openNewAutomation}>
                + Create Automation
              </button>
            )}
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .grid-2 {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }
        .badge-navy {
          background: var(--navy);
          color: var(--gold-light);
          padding: 2px 8px;
          border-radius: 10px;
          font-size: 9px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .btn-success {
          background: var(--green) !important;
          color: white !important;
          border: none !important;
        }
        .btn-success:hover {
          background: var(--green-dark) !important;
          color: white !important;
        }
        .btn-secondary {
          background: var(--text-light) !important;
          color: white !important;
          border: none !important;
        }
        .btn-secondary:hover {
          background: var(--text-mid) !important;
          color: white !important;
        }
        .btn-gold {
          background: var(--gold) !important;
          color: var(--navy) !important;
          border: none !important;
        }
        .btn-gold:hover {
          background: var(--gold-dark) !important;
          color: var(--navy) !important;
        }
        .btn-gold:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .btn-ghost {
          background: transparent !important;
          color: var(--text) !important;
          border: 1px solid var(--border) !important;
        }
        .btn-ghost:hover {
          background: var(--cream) !important;
        }
        .btn-danger {
          background: var(--red) !important;
          color: white !important;
          border: none !important;
        }
        .btn-danger:hover {
          background: var(--red-dark) !important;
          color: white !important;
        }
        .btn-sm {
          padding: 4px 10px !important;
          font-size: 12px !important;
          border-radius: 6px !important;
        }
        .form-hint {
          font-size: 11px;
          color: var(--text-light);
          margin-top: 4px;
        }
        .btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        @media (max-width: 768px) {
          .grid-2 {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </>
  );
};

export default Automation;